// firstRunPreview.jsx — replay the first-time onboarding on your own machine.
//
// Braeden 2026-09-06: "just for right now, can you make it so like my computer
// can use the first time onboarding so i can keep working on it till its
// perfect"
//
// ── WHY ?firstrun=1 WASN'T ENOUGH ─────────────────────────────────────────
// That flag (App.jsx `forceFirstRunButton`) only forces the "First Time?"
// button to RENDER while signed in. Everything behind the button is still you:
// the same `aria_user_id`, so the tour's server-side state, her memory of you
// and your business grounding are all already warm; the same session token, so
// you are signed in; the same local flags saying the intro was spoken and the
// first-run UX is complete. You get the button, not the experience.
//
// A first time is a stranger. So this mints one:
//
//   ?firstrun=new    stash every aria_*/howto_* key, mint a brand-new
//                    anonymous id, reload into the forced-button landing
//   ?firstrun=exit   put your own account back, exactly as it was
//
// Restart and Exit are also one click each on the badge, so the loop while
// tuning is: watch it, hit Restart, watch it again.
//
// ── IT IS A SANDBOX, NOT A LOGOUT ─────────────────────────────────────────
// Nothing is destroyed. The snapshot is taken ONCE on entry and survives any
// number of Restarts, so however many times you replay, Exit still restores
// the account you started with. Only keys this app owns (aria_*, howto_*) are
// touched, and the theme is deliberately left alone so the look doesn't flip
// underneath you mid-review.
//
// The new id is prefixed `test_` on purpose: `sweep_*` resolves to the OWNER
// in ariaOwnerScope, which would hand the "stranger" your business brain and
// make the replay a lie.

import React, { useState } from 'react';

const SNAPSHOT_KEY = 'aria_firstrun_snapshot';
const FLAG_KEY = 'aria_firstrun_preview';

/** Not onboarding state — flipping the theme mid-review helps nobody. */
const KEEP = new Set(['howto_theme']);

const OURS = /^(aria_|howto_)/i;

function ls() {
  try { return window.localStorage; } catch (_) { return null; }
}

/** Lift every key this app owns out of localStorage and return them. */
function takeOurKeys() {
  const store = ls();
  const taken = {};
  if (!store) return taken;
  const keys = [];
  for (let i = 0; i < store.length; i++) keys.push(store.key(i));
  keys.forEach((k) => {
    if (!k || k === SNAPSHOT_KEY || k === FLAG_KEY) return;
    if (KEEP.has(k) || !OURS.test(k)) return;
    try {
      taken[k] = store.getItem(k);
      store.removeItem(k);
    } catch (_) {}
  });
  return taken;
}

function freshVisitorId() {
  return 'test_firstrun_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function landing(params) {
  const path = (typeof window !== 'undefined' && window.location.pathname) || '/';
  return path + (params || '');
}

export function isPreviewOn() {
  const store = ls();
  try { return !!store && store.getItem(FLAG_KEY) === '1'; } catch (_) { return false; }
}

/** Become a stranger. Safe to call again while already previewing. */
export function startPreview() {
  const store = ls();
  if (!store) return;
  let hadSnapshot = false;
  try { hadSnapshot = !!store.getItem(SNAPSHOT_KEY); } catch (_) {}
  const taken = takeOurKeys();
  // Only the FIRST entry is your real account. A Restart must not overwrite it
  // with the previous throwaway visitor, or Exit has nothing to put back.
  if (!hadSnapshot) {
    try { store.setItem(SNAPSHOT_KEY, JSON.stringify(taken)); } catch (_) {}
  }
  try {
    store.setItem(FLAG_KEY, '1');
    store.setItem('aria_user_id', freshVisitorId());
  } catch (_) {}
  window.location.replace(landing('?firstrun=1'));
}

/** Put your own account back. */
export function exitPreview() {
  const store = ls();
  if (!store) return;
  let snap = null;
  try { snap = JSON.parse(store.getItem(SNAPSHOT_KEY) || 'null'); } catch (_) { snap = null; }
  takeOurKeys();                       // clear the throwaway visitor's keys
  try {
    store.removeItem(SNAPSHOT_KEY);
    store.removeItem(FLAG_KEY);
  } catch (_) {}
  if (snap && typeof snap === 'object') {
    Object.keys(snap).forEach((k) => {
      try { if (snap[k] != null) store.setItem(k, snap[k]); } catch (_) {}
    });
  }
  window.location.replace(landing());
}

/**
 * Read ?firstrun= and act on it. Called from main.jsx BEFORE React renders —
 * App reads localStorage in its state initializers, so a reset that happened
 * after the first paint would be a paint too late.
 */
export function applyFirstRunPreview() {
  if (typeof window === 'undefined') return;
  let mode = null;
  try { mode = new URLSearchParams(window.location.search).get('firstrun'); } catch (_) { return; }
  if (!mode) return;
  const m = String(mode).toLowerCase();
  if (m === 'new' || m === 'fresh' || m === 'reset') startPreview();
  else if (m === 'exit' || m === 'off' || m === 'me') exitPreview();
  // '1' is the pre-existing flag App.jsx reads itself — left alone.
}

/**
 * The way out. Renders only while previewing, so it can never show up for a
 * real visitor — and while it IS showing, there is never a doubt about whether
 * what you are looking at is the real first-time experience or your own account.
 */
export function FirstRunPreviewBadge() {
  const [on] = useState(isPreviewOn);
  if (!on) return null;
  return (
    <div className="firstrun-badge" role="status">
      <span className="firstrun-badge__dot" aria-hidden="true" />
      <span className="firstrun-badge__label">First-run preview</span>
      <button type="button" className="firstrun-badge__btn" onClick={startPreview}>Restart</button>
      <button type="button" className="firstrun-badge__btn firstrun-badge__btn--exit" onClick={exitPreview}>Exit</button>
    </div>
  );
}

export default FirstRunPreviewBadge;
